import { apiErrorMessage } from '@fieldvision/web-core';
import { MutationCache } from '@tanstack/react-query';

import { notify } from './notify.js';

/**
 * Every write in this console confirms itself, and no write fails silently.
 *
 * THE PROBLEM THIS SOLVES IS DISCIPLINE, not plumbing. There are ~45 mutations
 * across twenty files. Before this, success was announced by exactly one of them
 * and a failure was reported only where the author had remembered to render a
 * banner — so "did that save?" was answered by the row list refreshing, and a
 * row-button action that failed did nothing observable at all.
 *
 * Handling it in the cache rather than at each call site inverts the default:
 * feedback is what happens unless a mutation opts out, INCLUDING for mutations
 * that do not exist yet. That is the same reasoning as the sign-out subscription
 * in `main.tsx` — a rule that has to be re-applied by hand at every new call
 * site is a rule that is eventually not applied.
 *
 * HOW THE TWO SURFACES DIVIDE:
 *
 *   - A mutation driven by a FORM keeps its inline banner and sets
 *     `inlineError`. The dialog stays open on failure and the message belongs
 *     beside the fields that caused it — a toast in the corner is the wrong
 *     place to read "Nama: minimal 3 karakter", and reporting it twice trains
 *     people to ignore both.
 *   - Everything else — a row's Aktifkan button, a settings panel, a period
 *     close — has no such surface, and its failure goes to a toast.
 *   - Success is ALWAYS a toast, because the dialog that would have shown it has
 *     closed by then. It is opt-in only because the sentence is specific to the
 *     entity ("Channel ditambahkan.") and no default could write it.
 */
export type MutationFeedback = {
  /**
   * What to say when this write lands, verbatim.
   *
   * A PLAIN STRING, not a function of the response. When the sentence depends on
   * what was written — "diaktifkan" versus "dinonaktifkan" from the same
   * endpoint — call `notify.success` from the mutation's own `onSuccess`, where
   * `variables` is typed by `mutationFn` and needs no cast. Keeping this field
   * flat is what lets the common case stay one line.
   */
  success?: string;
  /**
   * This mutation renders its own error and must not also be toasted.
   *
   * Set it on anything whose failure appears in a `Banner` or in a
   * `ConfirmDialog`'s `error` prop.
   */
  inlineError?: boolean;
};

/*
 * A TYPE ALIAS RATHER THAN AN INTERFACE, and that is load-bearing. TanStack
 * resolves `MutationMeta` through `Register extends { mutationMeta: infer T } ? T
 * extends Record<string, unknown> ? T : Record<string, unknown> : …`, and an
 * interface has no implicit index signature — so an interface here silently
 * fails that constraint and every `meta` in the app degrades back to
 * `Record<string, unknown>`. It still compiles; it just stops checking.
 */

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationFeedback;
  }
}

export function feedbackMutationCache(): MutationCache {
  return new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const { success } = mutation.meta ?? {};
      if (success !== undefined) notify.success(success);
    },

    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.inlineError === true) return;
      notify.error(apiErrorMessage(error));
    },
  });
}
